/**
 * Misuratore radiale (meter): una sola quota rispetto a un limite del 100%.
 * Il riempimento porta la severità; la traccia è una tinta chiara dello stesso
 * colore, così lo stato si legge su tutto l'arco.
 *
 * I colori di stato non viaggiano mai da soli: ogni gauge espone anche
 * icona + etichetta testuale, perché verde e ambra collidono sotto daltonismo.
 */

// Passi validati sulla superficie chiara (contrasto >= 3:1)
const SEVERITA = {
  buono:      { fill: '#0ca30c', track: '#d8f0d8', label: 'Buono',      icona: '●' },
  attenzione: { fill: '#c07d05', track: '#f6e6c9', label: 'Attenzione', icona: '▲' },
  critico:    { fill: '#d03b3b', track: '#f7dcdc', label: 'Critico',    icona: '■' },
  nd:         { fill: '#8a8a85', track: '#e8e8e4', label: 'Dato assente', icona: '–' },
};

function livello(pct, soglie) {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return 'nd';
  if (pct >= soglie[0]) return 'buono';
  if (pct >= soglie[1]) return 'attenzione';
  return 'critico';
}

// Punto sull'arco per una percentuale (semicerchio: 180° a sinistra → 0° a destra)
function punto(cx, cy, r, pct) {
  const rad = (Math.PI * (180 - pct * 180)) / 180;
  return [cx + r * Math.cos(rad), cy - r * Math.sin(rad)];
}

function arco(cx, cy, r, da, a) {
  const [x1, y1] = punto(cx, cy, r, da);
  const [x2, y2] = punto(cx, cy, r, a);
  // Il quadrante utile è un semicerchio: nessun tratto supera i 180°,
  // quindi large-arc resta sempre 0 (con 1 l'arco verrebbe disegnato al contrario).
  return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`;
}

import { useEffect, useRef, useState } from 'react';

/** Corsa della lancetta: parte veloce e frena in prossimità del valore. */
const easeOut = t => 1 - Math.pow(1 - t, 3);

/**
 * Anima da 0 al valore indicato ogni volta che questo cambia
 * (apertura pagina, cambio mese/anno).
 */
function useCorsaLancetta(target, durata = 900) {
  const [v, setV] = useState(0);
  const raf = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (target === null || target === undefined || Number.isNaN(target)) { setV(0); return; }

    // Nessuna animazione se l'utente la evita o se la scheda non è in primo piano:
    // con la pagina nascosta requestAnimationFrame è sospeso e il misuratore
    // resterebbe fermo a 0, mostrando un dato falso.
    const animazioneInutile =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || document.hidden;
    if (animazioneInutile) { setV(target); return; }

    const t0 = performance.now();
    const passo = (ora) => {
      const p = Math.min(1, (ora - t0) / durata);
      setV(target * easeOut(p));
      if (p < 1) raf.current = requestAnimationFrame(passo);
    };
    setV(0);
    raf.current = requestAnimationFrame(passo);

    // Rete di sicurezza: comunque vada, a fine corsa il valore è quello vero
    timer.current = setTimeout(() => setV(target), durata + 200);

    return () => { cancelAnimationFrame(raf.current); clearTimeout(timer.current); };
  }, [target, durata]);

  // Tornando sulla scheda la lancetta rifà la corsa
  useEffect(() => {
    const alRitorno = () => {
      if (document.hidden || target === null || target === undefined) return;
      setV(target);
    };
    document.addEventListener('visibilitychange', alRitorno);
    return () => document.removeEventListener('visibilitychange', alRitorno);
  }, [target]);

  return v;
}

export default function GaugeChart({
  valore,            // percentuale 0-100, oppure null
  titolo,
  sottotitolo,
  dettaglio,         // testo mostrato al passaggio del mouse
  soglie = [50, 25], // [soglia "buono", soglia "attenzione"]
}) {
  const pct = valore === null || valore === undefined ? null : Math.max(0, Math.min(100, Number(valore)));
  const animato = useCorsaLancetta(pct);      // valore in corsa, da 0 al dato
  const frazione = pct === null ? 0 : animato / 100;
  // Colore e stato seguono il valore finale: non devono cambiare durante la corsa
  const s = SEVERITA[livello(pct, soglie)];

  const W = 200, H = 118;
  const cx = W / 2, cy = 104, r = 78, spessore = 15;

  return (
    <div className="card flex flex-col items-center" title={dettaglio || undefined}>
      <div className="text-sm text-gray-500 self-start">{titolo}</div>
      {sottotitolo && <div className="text-xs text-gray-400 self-start mb-1">{sottotitolo}</div>}

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-[200px] mt-1" role="img"
           aria-label={`${titolo}: ${pct === null ? 'dato non disponibile' : pct.toFixed(1) + '%'} — ${s.label}`}>
        {/* traccia: tinta chiara dello stesso colore */}
        <path d={arco(cx, cy, r, 0, 1)} fill="none" stroke={s.track}
              strokeWidth={spessore} strokeLinecap="round" />
        {/* valore in corsa */}
        {pct !== null && frazione > 0.001 && (
          <path d={arco(cx, cy, r, 0, frazione)} fill="none" stroke={s.fill}
                strokeWidth={spessore} strokeLinecap="round" />
        )}
        {/* punta della lancetta */}
        {pct !== null && frazione > 0.001 && (() => {
          const [px, py] = punto(cx, cy, r, frazione);
          return <circle cx={px} cy={py} r={spessore / 2 + 2} fill={s.fill}
                         stroke="#ffffff" strokeWidth="2" />;
        })()}
        {/* estremi della scala */}
        <text x={cx - r} y={cy + 16} textAnchor="middle" fontSize="9" fill="#8a8a85">0%</text>
        <text x={cx + r} y={cy + 16} textAnchor="middle" fontSize="9" fill="#8a8a85">100%</text>
        {/* valore in inchiostro, mai nel colore della serie */}
        <text x={cx} y={cy - 16} textAnchor="middle" fontSize="34" fontWeight="700" fill="#0b0b0b">
          {pct === null ? '—' : `${animato.toFixed(1)}%`}
        </text>
      </svg>

      {/* stato: colore + icona + etichetta, mai colore da solo */}
      <div className="flex items-center gap-1.5 mt-1">
        <span aria-hidden="true" style={{ color: s.fill }} className="text-xs leading-none">{s.icona}</span>
        <span className="text-xs font-medium text-gray-600">{s.label}</span>
      </div>
      {dettaglio && <div className="text-xs text-gray-400 mt-1 text-center">{dettaglio}</div>}
    </div>
  );
}
